import React from "react";

import cx from "classnames";

import { doNothing } from "@kazoohr/helpers";

import styles from "./Radio.scss";

/**
 * Available sizes for the Radio component
 */
export const SIZES = {
  XSMALL: "extra-small",
  SMALL: "small",
  REGULAR: "regular",
} as const;

export type RadioSize = (typeof SIZES)[keyof typeof SIZES];

/**
 * Radio Component Props
 */
export interface RadioProps<TValue extends string | number = string | number> {
  /** Whether the radio is checked */
  checked?: boolean;
  /** Optional class name for custom styling */
  className?: string;
  /** Whether the radio is disabled */
  disabled?: boolean;
  /** Whether the radio has an error state */
  hasError?: boolean;
  /** Optional helper text displayed below the radio */
  helperText?: React.ReactNode;
  /** Unique identifier for the radio (also links input to label) */
  id: TValue;
  /** Test ID for testing purposes */
  "data-testid"?: string;
  /** The label text or element */
  label: React.ReactNode;
  /** Radio name attribute (links radio buttons together) */
  name?: string;
  /** Callback when radio is changed */
  onChange?: (id: TValue) => void;
  /** Size of the radio component */
  size?: RadioSize;
}

/**
 * A Radio button component with built in label and helper text.
 *
 * Notes:
 * - The provided `label` has some default styling, but is easily controllable via passing in a `Text` component with specific props set.
 * - The helper text is not located within the label, as it should not toggle the radio. It is provided as a sibling element.
 */
export function Radio<TValue extends string | number = string | number>({
  checked,
  className,
  disabled,
  helperText,
  id,
  label,
  name,
  onChange = doNothing,
  size = SIZES.SMALL,
  hasError,
  "data-testid": dataTestId,
}: RadioProps<TValue>): JSX.Element {
  const isText = typeof label === "string";
  const classNames = cx(
    {
      [styles["wrapperText"]]: isText,
      [styles["wrapperOthers"]]: !isText,
    },
    {
      [styles["wrapper--xsmall"]]: size === SIZES.XSMALL,
      [styles["wrapper--small"]]: size === SIZES.SMALL,
    },
    className
  );
  const inputWrapper = styles.wrapperInput;
  const spanClassNames = cx(
    {
      [styles["error"]]: hasError,
    },
    styles.radio
  );

  return (
    <div data-testid="wrapper-label" className={classNames}>
      <div className={inputWrapper}>
        <input
          type="radio"
          className={styles.input}
          checked={checked}
          disabled={disabled}
          id={String(id)}
          data-testid={dataTestId}
          name={name}
          onChange={() => onChange(id)}
        />
        <span className={spanClassNames} />
        <label
          data-testid="radio-label"
          className={styles.label}
          htmlFor={String(id)}
        >
          {label}
        </label>
      </div>

      {helperText && (
        <div
          className={cx(styles["helper-row"], {
            [styles["helper-text-disabled"]]: disabled,
          })}
          data-testid="radio-helper-text"
        >
          {helperText}
        </div>
      )}
    </div>
  );
}

Radio.displayName = "Radio";
Radio.SIZES = SIZES;

export { Radio as default };
